import React, { useState } from "react";
import { Folder as FolderIcon, Trash2, Pencil } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Snackbar, Alert, TextField
} from "@mui/material";
import axios from "axios";

const Folder = ({ folder, parentPath, userID, onSuccess }) => {
  const role = localStorage.getItem("type");
  const fullPath = parentPath ? `${parentPath}/${folder.name}` : folder.name;

  const [openDelete, setOpenDelete] = useState(false);
  const [openRename, setOpenRename] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState(folder.name);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/doc-vault/delete-folder`,
        { path: fullPath, userID },
        { withCredentials: true }
      );

      if (response.status === 200) {
        setOpenDelete(false);
        setSnackbar({ open: true, message: "Folder deleted successfully", severity: "success" });
        onSuccess(); // Callback to refresh the folder list
      } else {
        setSnackbar({ open: true, message: "Failed to delete folder", severity: "error" });
      }
    } catch (error) {
      console.error("Delete error:", error);
      setSnackbar({ open: true, message: "An error occurred while deleting the folder", severity: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const handleRename = async () => {
    if (newName.trim() === "" || newName === folder.name) {
      setSnackbar({ open: true, message: "Enter a new valid name", severity: "warning" });
      return;
    }

    setRenaming(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/doc-vault/rename`,
        {
          path: fullPath,
          userID,
          newName: newName
        },
        { withCredentials: true }
      );

      if (response.status === 200) {
        setOpenRename(false);
        setSnackbar({ open: true, message: "Folder renamed successfully", severity: "success" });
        onSuccess();
      } else {
        setSnackbar({ open: true, message: "Failed to rename folder", severity: "error" });
      }
    } catch (error) {
      console.error("Rename error:", error);
      setSnackbar({ open: true, message: "An error occurred while renaming the folder", severity: "error" });
    } finally {
      setRenaming(false);
    }
  };

  return (
    <>
      <div className="relative">
        {/* Folder Card */}
        <Link
          to={`/${role}/docvault/${fullPath}`}
          className="block relative rounded-xl p-5 
            bg-gray-50 
            hover:bg-blue-50 
            border border-gray-200 hover:border-blue-200
            shadow-sm hover:shadow-md
            transition-all duration-300 ease-in-out 
            transform hover:-translate-y-1 
            focus:outline-none focus:ring-2 focus:ring-blue-300
            group"
        >
          <div className="flex flex-col items-center justify-center text-center">
            <div className="mb-3 p-3">
              <FolderIcon 
                className="w-8 h-8 text-blue-600 
                group-hover:text-blue-700 
                transition-colors duration-200"
              />
            </div>
            <span 
              className="text-sm font-medium text-gray-800 
              group-hover:text-blue-700 
              truncate max-w-full 
              transition-colors duration-200"
            >
              {folder.name}
            </span>
          </div>
        </Link>

        {/* Rename Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            setNewName(folder.name); // Reset input
            setOpenRename(true);
          }}
          className="absolute top-2 right-10 p-1 rounded-full bg-white hover:bg-yellow-50 border border-gray-200 shadow transition-all z-10"
        >
          <Pencil className="w-4 h-4 text-gray-500 hover:text-yellow-600 transition-colors" />
        </button>

        {/* Delete Button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            setOpenDelete(true);
          }}
          className="absolute top-2 right-2 p-1 rounded-full bg-white hover:bg-red-50 border border-gray-200 shadow transition-all z-10"
        >
          <Trash2 className="w-4 h-4 text-gray-500 hover:text-red-600 transition-colors" />
        </button>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Delete Folder</DialogTitle>
        <DialogContent>
          Are you sure you want to delete the folder <strong>{folder.name}</strong>?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)} color="primary" disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={openRename} onClose={() => setOpenRename(false)}>
        <DialogTitle>Rename Folder</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New Folder Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            disabled={renaming}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRename(false)} color="primary" disabled={renaming}>
            Cancel
          </Button>
          <Button onClick={handleRename} color="success" disabled={renaming}>
            {renaming ? "Renaming..." : "Rename"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Folder;
